/**
 * Pane layout — two-pane horizontal split with optional 3rd inspector pane.
 *
 * Hosts a vertical drag-handle splitter between the Edit and Preview panes,
 * persists the split fraction (0–1) and pane-swap state to localStorage, and
 * mounts a hidden 3rd Inspector pane toggled via a hotkey.
 *
 *
 * Public API:
 *
 *     const layout = createPaneLayout({
 *         workspace, paneEditor, panePreview, paneInspector,
 *         splitterEls, swapBtn, inspectorCloseBtns,
 *     });
 *     layout.applyLayout();    // recompute & paint after a resize
 *     layout.toggleInspector();
 *     layout.toggleSwap();
 *     layout.getState();       // { splitFraction, swapped, inspectorOpen }
 *
 * Constants exported for tests:
 *     DEFAULT_SPLIT_FRACTION, MIN_EDIT_PANE_PX, MIN_PREVIEW_PANE_PX,
 *     SPLITTER_WIDTH_PX, SPLITTER_KEY_STEP
 *
 * @module pane-layout
 */
// ─── Constants ────────────────────────────────────────────────────────────────
export const PANE_SPLIT_KEY = "editor:paneSplit";
export const PANE_SWAP_KEY = "editor:swapPanes";
export const DEFAULT_SPLIT_FRACTION = 0.45;
export const MIN_EDIT_PANE_PX = 280;
export const MIN_PREVIEW_PANE_PX = 320;
// The splitter is `position: absolute` overlay — it occupies no flex column,
// so we don't reserve width for it. (Was 6 prior to the 260504 splitter
// collapse fix; the splitter's hit area is now 8 px centered on the pane
// seam but still position: absolute, so usable width is the full container.)
export const SPLITTER_WIDTH_PX = 0;
export const SPLITTER_KEY_STEP = 0.02;
const SPLITTER_MIN_FRACTION = 0.1;
const SPLITTER_MAX_FRACTION = 0.9;
// ─── Pure helpers ─────────────────────────────────────────────────────────────
/**
 * Read a 0..1 split fraction from localStorage; fall back to DEFAULT.
 *
 * @param storage for tests; defaults to globalThis.localStorage
 */
export function readPersistedSplitFraction(storage) {
    try {
        const s = storage ?? (typeof localStorage !== "undefined" ? localStorage : null);
        if (!s)
            return DEFAULT_SPLIT_FRACTION;
        const raw = s.getItem(PANE_SPLIT_KEY);
        const v = raw == null ? NaN : parseFloat(raw);
        return Number.isFinite(v) && v > 0 && v < 1 ? v : DEFAULT_SPLIT_FRACTION;
    }
    catch {
        // localStorage unavailable (private mode); use default
        return DEFAULT_SPLIT_FRACTION;
    }
}
/**
 * Read swap flag from localStorage.
 *
 * @param storage for tests; defaults to globalThis.localStorage
 */
export function readPersistedSwap(storage) {
    try {
        const s = storage ?? (typeof localStorage !== "undefined" ? localStorage : null);
        if (!s)
            return false;
        return s.getItem(PANE_SWAP_KEY) === "1";
    }
    catch {
        return false;
    }
}
/**
 * Compute pane pixel widths for a given workspace width + split fraction +
 * swap state, honoring the per-pane minimum widths. Pure — no DOM access.
 */
export function computePaneWidths(containerWidth, splitFraction) {
    const usableWidth = Math.max(0, containerWidth - SPLITTER_WIDTH_PX);
    let editPx = Math.floor(usableWidth * splitFraction);
    editPx = Math.max(MIN_EDIT_PANE_PX, Math.min(usableWidth - MIN_PREVIEW_PANE_PX, editPx));
    const previewPx = usableWidth - editPx;
    return { editPx, previewPx, usableWidth };
}
/**
 * Clamp a candidate split fraction so neither pane drops below its
 * minimum width for a given container size.
 */
export function clampFraction(candidate, containerWidth) {
    if (containerWidth <= 0)
        return candidate;
    const minFrac = MIN_EDIT_PANE_PX / containerWidth;
    const maxFrac = 1 - (MIN_PREVIEW_PANE_PX / containerWidth);
    return Math.max(minFrac, Math.min(maxFrac, candidate));
}
// ─── createPaneLayout ─────────────────────────────────────────────────────────
/**
 * Wire the pane-layout controller against an existing DOM. Returns a
 * controller handle the host can drive (resize re-paint, hotkey toggle,
 * swap-button click).
 */
export function createPaneLayout(opts) {
    const { workspace, paneEditor, panePreview, paneInspector, splitterEls = [], swapBtn = null, inspectorCloseBtns = [], storage, win = (typeof window !== "undefined" ? window : null), } = opts;
    const _storage = storage ?? (typeof localStorage !== "undefined" ? localStorage : null);
    let _splitFraction = readPersistedSplitFraction(_storage ?? undefined);
    let _swapped = readPersistedSwap(_storage ?? undefined);
    let _inspectorOpen = false;
    function _saveSplit() {
        try {
            if (_storage)
                _storage.setItem(PANE_SPLIT_KEY, String(_splitFraction));
        }
        catch { /* best-effort; localStorage may be unavailable */ }
    }
    function _saveSwap() {
        try {
            if (_storage)
                _storage.setItem(PANE_SWAP_KEY, _swapped ? "1" : "0");
        }
        catch { /* best-effort; localStorage may be unavailable */ }
    }
    function applyLayout() {
        if (!workspace)
            return;
        // Set flex direction so swap can flip the visual order.
        workspace.style.flexDirection = _swapped ? "row-reverse" : "row";
        // Mirror swap state on a data attribute so CSS can re-target the
        // splitter to the correct visual edge of the (DOM-fixed) preview
        // pane: when not swapped, splitter sits on preview's RIGHT (the
        // seam); when swapped, preview is on the right and the seam is on
        // its LEFT, so the splitter needs to flip.
        workspace.dataset.swapped = _swapped ? "true" : "false";
        const containerWidth = workspace.clientWidth;
        if (containerWidth > 0) {
            const { editPx, previewPx } = computePaneWidths(containerWidth, _splitFraction);
            if (paneEditor) {
                paneEditor.style.flexBasis = `${editPx}px`;
                paneEditor.style.width = `${editPx}px`;
            }
            if (panePreview) {
                panePreview.style.flexBasis = `${previewPx}px`;
                panePreview.style.width = `${previewPx}px`;
            }
        }
        if (paneInspector) {
            paneInspector.classList.toggle("hidden", !_inspectorOpen);
        }
    }
    function toggleInspector() {
        _inspectorOpen = !_inspectorOpen;
        applyLayout();
    }
    function toggleSwap() {
        _swapped = !_swapped;
        applyLayout();
        _saveSwap();
    }
    // Wire the splitter pointer drag.
    let _dragging = false;
    let _dragStartX = 0;
    let _dragStartFraction = 0;
    function onPointerDown(ev) {
        _dragging = true;
        _dragStartX = ev.clientX;
        _dragStartFraction = _splitFraction;
        if (win && win.document && win.document.body) {
            win.document.body.style.cursor = "col-resize";
            win.document.body.style.userSelect = "none";
        }
        ev.preventDefault();
    }
    function onPointerMove(ev) {
        if (!_dragging || !workspace)
            return;
        const rect = workspace.getBoundingClientRect();
        if (rect.width <= 0)
            return;
        const dx = ev.clientX - _dragStartX;
        // Default layout: PDF (panePreview) LEFT, Structure (paneEditor) RIGHT.
        // The splitter sits on the right edge of panePreview. Dragging RIGHT
        // grows panePreview (left) and shrinks paneEditor (right), so deltaFrac
        // must be negative (sign = -1). When swapped (paneEditor LEFT), the
        // geometry reverses: dragging right grows paneEditor, so sign = +1.
        const sign = _swapped ? 1 : -1;
        const deltaFrac = (dx / rect.width) * sign;
        _splitFraction = clampFraction(_dragStartFraction + deltaFrac, rect.width);
        applyLayout();
    }
    function onPointerUp() {
        if (!_dragging)
            return;
        _dragging = false;
        if (win && win.document && win.document.body) {
            win.document.body.style.cursor = "";
            win.document.body.style.userSelect = "";
        }
        _saveSplit();
    }
    if (win) {
        win.addEventListener("pointermove", onPointerMove);
        win.addEventListener("pointerup", onPointerUp);
    }
    for (const splitter of splitterEls) {
        splitter.addEventListener("pointerdown", onPointerDown);
        splitter.addEventListener("keydown", (ev) => {
            const kev = ev;
            // Default layout: panePreview LEFT, paneEditor RIGHT.
            // ArrowLeft narrows the left pane (grows right) → increase editPx → increase fraction.
            // ArrowRight widens the left pane (shrinks right) → decrease editPx → decrease fraction.
            // When swapped: paneEditor LEFT, so the directions flip back.
            const keySign = _swapped ? 1 : -1;
            if (kev.key === "ArrowLeft") {
                kev.preventDefault();
                _splitFraction = Math.max(SPLITTER_MIN_FRACTION, _splitFraction + keySign * SPLITTER_KEY_STEP);
                applyLayout();
                _saveSplit();
            }
            else if (kev.key === "ArrowRight") {
                kev.preventDefault();
                _splitFraction = Math.min(SPLITTER_MAX_FRACTION, _splitFraction - keySign * SPLITTER_KEY_STEP);
                applyLayout();
                _saveSplit();
            }
        });
    }
    if (swapBtn) {
        swapBtn.addEventListener("click", () => toggleSwap());
    }
    for (const btn of inspectorCloseBtns) {
        btn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            _inspectorOpen = false;
            applyLayout();
        });
    }
    return {
        applyLayout,
        toggleInspector,
        toggleSwap,
        getState: () => ({
            splitFraction: _splitFraction,
            swapped: _swapped,
            inspectorOpen: _inspectorOpen,
        }),
    };
}
